// src/ui/uiUtils.js - Refactored for General Toast & Game Over Logic v3
import { auth, db } from '../firebaseConfig.js';
import UIController from './uiController.js'; // Use UIController for showing overlays

/**
 * Shows a generic, non-blocking message toast usually at the top of the screen.
 */
export function showGeneralMessage(message, duration = 2500, isError = false) {
    const containerId = 'general-message-toast';
    let container = document.getElementById(containerId);

    if (!container) {
        container = document.createElement('div');
        container.id = containerId;
        // Apply basic toast styles inline for fallback (can be overridden by CSS)
        Object.assign(container.style, {
            position: 'fixed', top: '15px', left: '50%', transform: 'translateX(-50%)',
            padding: '10px 20px', borderRadius: 'var(--border-radius, 4px)', zIndex: '10001',
            opacity: '0', transition: 'opacity 0.3s ease, transform 0.3s ease', textAlign: 'center',
            fontFamily: 'var(--font-family, monospace)', fontSize: 'var(--font-size-sm, 14px)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.3)', maxWidth: '80%',
            transform: 'translate(-50%, -20px)' // Start slightly above
        });
        document.body.appendChild(container);
    }

    container.textContent = message;
    // Use CSS variables for colors if available, otherwise fallback
    const errorBg = getComputedStyle(document.documentElement).getPropertyValue('--color-danger').trim() || '#D32F2F';
    const successBg = getComputedStyle(document.documentElement).getPropertyValue('--color-success').trim() || '#388E3C';
    container.style.backgroundColor = isError ? errorBg : successBg;
    container.style.color = '#FFFFFF'; // White text generally works

    // Clear previous timer if exists
    if (container.timer) clearTimeout(container.timer);

    // Animate in
    requestAnimationFrame(() => {
        container.style.opacity = '1';
        container.style.transform = 'translateX(-50%)';
    });


    // Auto-hide
    container.timer = setTimeout(() => {
        container.style.opacity = '0';
        container.style.transform = 'translate(-50%, -20px)';
        container.timer = null;
        // Don't remove from DOM, reuse is better
    }, duration);

    return container;
}

/**
* Populates the game over overlay content and then shows it using UIController.
* @param {number} finalScore - Final game score
* @param {string} gameType - Type of game (e.g., "Shortcut", "PPTHero")
*/
export async function showGameOverUI(finalScore, gameType = "Shortcut") {
    const overlayId = 'gameover-overlay';
    const listDiv = document.getElementById("gameover-list");
    const titleElement = document.getElementById("gameover-title");
    const buttonContainer = document.querySelector(`#${overlayId} .button-group`);

    if (!listDiv || !titleElement) {
        console.error("Game over overlay content or title element not found!");
        showGeneralMessage("Error showing game results.", 3000, true);
        return;
    }

    // Set initial state
    titleElement.textContent = `GAME OVER`;
    listDiv.innerHTML = `<div class="loading-indicator"><div class="spinner"></div>Loading results...</div>`; // Use standard loading indicator
    if(buttonContainer) buttonContainer.style.visibility = 'hidden'; // Hide buttons while loading

    // Show the overlay structure immediately using UIController
    UIController.showOverlay(overlayId);

    try {
        // Build leaderboard content async
        await buildGameOverLeaderboard(listDiv, finalScore, gameType);

        // Check auth state AFTER leaderboard is built
        const user = auth.currentUser;

        // Append sign-in/verify prompt if needed
        if (!user) {
            const signInPrompt = createPromptButton(
                "Sign In to save score & compete!",
                () => { UIController.hideOverlay(overlayId); UIController.showOverlay('auth-overlay'); },
                 'var(--color-secondary)' // Cyan
            );
             if (signInPrompt) listDiv.appendChild(signInPrompt);
        } else if (!user.emailVerified) {
             const verifyPrompt = createPromptButton(
                "Verify Email to save future scores!",
                () => { UIController.hideOverlay(overlayId); UIController.showOverlay('auth-overlay'); },
                'var(--color-warning)' // Yellow
            );
             if (verifyPrompt) listDiv.appendChild(verifyPrompt);
        }

        // Show buttons again
        if(buttonContainer) buttonContainer.style.visibility = 'visible';

    } catch (error) {
        console.error("Error building game over UI:", error);
        listDiv.innerHTML = `<div class="error-message">Error loading leaderboard data.</div>`; // Use standardized error message
        // Still show buttons on error
        if(buttonContainer) buttonContainer.style.visibility = 'visible';
    }
}

// Helper to create prompt buttons (uses standard button styles where possible)
function createPromptButton(text, onClick, bgColorVar = 'var(--color-info)') {
    try {
        const button = document.createElement("button");
        button.textContent = text;
        button.style.display = 'block';
        button.style.width = 'calc(100% - 20px)';
        button.style.margin = '15px auto 0 auto';
        button.style.padding = 'var(--space-sm, 8px)'; // Use var with fallback
        button.style.fontSize = 'var(--font-size-sm, 14px)';
        button.style.backgroundColor = bgColorVar;
        button.style.color = 'var(--color-text-light, #FFF)';
        button.style.border = 'none';
        button.style.borderRadius = 'var(--border-radius, 4px)';
        button.style.cursor = 'pointer';
        button.style.fontFamily = 'var(--font-family, monospace)';
        button.style.fontWeight = 'bold';
        button.style.transition = 'background-color var(--transition-speed, 0.2s), transform var(--transition-speed, 0.2s)';

        button.addEventListener("mouseover", () => { button.style.transform = 'translateY(-1px)'; });
        button.addEventListener("mouseout", () => { button.style.transform = 'none'; });
        button.addEventListener("click", onClick);
        return button;
    } catch (e) {
        console.error("Error creating prompt button:", e);
        return null; // Return null on error
    }
}


/** Builds the leaderboard table HTML within the parentElement. */
async function buildGameOverLeaderboard(parentElement, finalScore, gameType) {
    parentElement.innerHTML = '<div class="loading-indicator"><div class="spinner"></div>Fetching leaderboard...</div>'; // Use standard loading

    try {
        const displayGameType = (gameType === "Shortcut" || gameType?.toLowerCase().includes("slide")) ? "Slide Wars" : gameType;
        const isPPT = gameType === "PPTHero";
        const gameTypesToQuery = isPPT ? ["PPTHero"] : ["Shortcut", "slide wars", "Slide Wars", "MultiplayerShortcut"];

        // --- Fetch Scores ---
        let allScores = [];
         // Directly fetch top 10 for this specific game type for game over screen
         try {
             const querySnapshot = await db.collection("scores")
               .where("game", "in", gameTypesToQuery) // Query relevant types
               .orderBy("score", "desc")
               .limit(20) // Fetch a bit more to find rank
               .get();
             querySnapshot.forEach(doc => allScores.push({ id: doc.id, ...doc.data() }));
         } catch (queryErr) { console.warn(`Firestore query failed for ${gameType}:`, queryErr); }


        // Add current score temporarily if user is logged out
         const user = auth.currentUser;
         if (!user) {
             const tempName = localStorage.getItem("tempSimpsonsName") || "Guest";
             allScores.push({
                 player: tempName, score: finalScore, game: gameType, timestamp: Date.now(),
                 isCurrentScore: true, verified: false
             });
         }

        // --- Process & Filter Scores ---
        const seenIds = new Set();
        const processedScores = allScores
            .map(data => {
                 // Skip duplicates if ID exists
                 if (data.id && seenIds.has(data.id)) return null;
                 if (data.id) seenIds.add(data.id);

                 // Basic check for score validity
                 if (typeof data.score !== 'number') return null;

                const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : (data.timestamp ? new Date(data.timestamp) : new Date());
                // Check verification deadline for unverified scores
                const deadline = data.deadline?.toDate ? data.deadline.toDate() : null;
                const isExpired = !data.verified && deadline && deadline < new Date();
                const isPlaceholderCurrent = data.isCurrentScore === true; // Check for the temp score added above

                // Include if verified, or unverified & not expired, or the temporary current score
                 if (data.verified || (!isExpired && !data.verified) || isPlaceholderCurrent) {
                     return {
                         player: data.player || "Player",
                         score: data.score,
                         verified: data.verified === true,
                          // Highlight if logged in user's score matches finalScore OR if it's the temporary score
                         isCurrentUserScore: (user && data.userUID === user.uid && data.score === finalScore),
                         isCurrentScore: isPlaceholderCurrent,
                         date: createdAt,
                     };
                 } else {
                     return null; // Exclude expired unverified scores
                 }
            })
            .filter(score => score !== null);

        // --- Sort and Rank ---
        processedScores.sort((a, b) => b.score - a.score);
        let playerRank = -1;
        processedScores.forEach((s, i) => {
            s.rank = i + 1; // Calculate rank based on sorted order
             // Highlight logic refined
             if (s.isCurrentUserScore || s.isCurrentScore) {
                 playerRank = s.rank;
                 s.highlight = true; // Add a highlight flag
             } else {
                 s.highlight = false;
             }
        });

        // --- Build Table HTML ---
        const top10 = processedScores.slice(0, 10);
        let tableHTML = `
            <h3 class="leaderboard-view-title" style="color: var(--color-primary); font-size: var(--font-size-lg);">${displayGameType} Top 10</h3>
            <table class="leaderboard-table">
                <thead>
                    <tr><th>Rank</th><th>Player</th><th>Score</th><th>Date</th></tr>
                </thead>
                <tbody>
        `;

        if (top10.length === 0) {
            tableHTML += `<tr><td colspan="4" class="no-scores">Be the first on the leaderboard!</td></tr>`;
        } else {
            top10.forEach(entry => {
                const highlightStyle = entry.highlight ? 'background-color: rgba(0, 255, 0, 0.1); font-weight: bold;' : '';
                const star = entry.verified ? '<span class="verified-star">★</span>' : '';
                const dateStr = entry.date ? entry.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '-';
                tableHTML += `
                    <tr style="${highlightStyle}">
                        <td class="rank">${entry.rank}</td>
                        <td class="player" title="${entry.player}">${entry.player}${star}</td>
                        <td class="score">${entry.score.toLocaleString()}</td>
                        <td class="date">${dateStr}</td>
                    </tr>
                `;
            });
        }

        tableHTML += `</tbody></table>`;

        // --- Player Rank Info ---
        let rankInfoHTML = '';
        if (playerRank !== -1) {
             rankInfoHTML = `<div style="margin-top: var(--space-sm); font-size: var(--font-size-sm); color: var(--color-text-medium);">Your score: ${finalScore.toLocaleString()} (Rank #${playerRank})</div>`;
        } else if (user) { // Show rank info only if logged in but not in top 10
             // Find the actual rank if outside top 10
             const actualRankIndex = processedScores.findIndex(s => s.isCurrentUserScore);
             if(actualRankIndex !== -1) {
                 playerRank = actualRankIndex + 1;
                 rankInfoHTML = `<div style="margin-top: var(--space-sm); font-size: var(--font-size-sm); color: var(--color-text-medium);">Your score: ${finalScore.toLocaleString()} (Rank #${playerRank})</div>`;
             } else {
                  rankInfoHTML = `<div style="margin-top: var(--space-sm); font-size: var(--font-size-sm); color: var(--color-text-medium);">Your score: ${finalScore.toLocaleString()} (Not in Top ${processedScores.length})</div>`;
             }
        } else {
             // For guests, just show score if rank not in top 10
             rankInfoHTML = `<div style="margin-top: var(--space-sm); font-size: var(--font-size-sm); color: var(--color-text-medium);">Your score: ${finalScore.toLocaleString()}</div>`;
        }


         // --- Legend ---
         const legendHTML = `<div class="leaderboard-note" style="opacity: 1;"><span class="verified-star">★</span> = Verified Player</div>`; // Use note class

        parentElement.innerHTML = tableHTML + rankInfoHTML + legendHTML; // Set final HTML

    } catch (error) {
        console.error("Error building game over leaderboard:", error);
        parentElement.innerHTML = '<div class="error-message">Could not load leaderboard.</div>'; // Use standard error message
    }
}


// --- Utility Functions ---

// Format time (Unchanged)
export function formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

// Loading Indicator utilities (Rely on CSS class, helpers maybe less needed)
export function createLoadingIndicator(message = "Loading...") {
    const div = document.createElement('div');
    div.className = 'loading-indicator'; // Use class from CSS
     div.innerHTML = `<div class="spinner"></div>${message}`; // Add spinner via HTML
    return div;
}
export function removeLoadingIndicator(loadingElement) {
    if (loadingElement && loadingElement.parentNode) {
        loadingElement.parentNode.removeChild(loadingElement);
    }
}


// --- Event Listeners for Game Over Buttons (Ensure Correct Scene Handling) ---
document.addEventListener("DOMContentLoaded", () => {
    const restartBtn = document.getElementById("gameover-restart-btn");
    const mainMenuBtn = document.getElementById("gameover-mainmenu-btn");
    const overlayId = 'gameover-overlay';

    if (restartBtn) {
        restartBtn.addEventListener("click", () => {
            UIController.hideOverlay(overlayId);
            setTimeout(() => {
                 const currentScene = window.gameInstance?.getActiveScene?.();
                 if (currentScene && typeof currentScene.scene?.restart === 'function') {
                     console.log(`Restarting scene: ${currentScene.scene.key}`);
                     try {
                         // Ensure physics is resumed if paused
                          if (currentScene.physics?.isPaused) currentScene.physics.resume();
                          currentScene.scene.restart();
                     } catch (e) {
                         console.error(`Error restarting scene ${currentScene.scene.key}:`, e);
                         // Fallback to main menu if restart fails catastrophically
                          window.gameInstance?.scene?.start("MainMenuScene");
                     }
                 } else {
                      console.warn("No active Phaser scene found or scene cannot be restarted.");
                      // Fallback if no active scene found
                       window.gameInstance?.scene?.start("MainMenuScene");
                 }
            }, 50); // Shorter delay
        });
    }

    if (mainMenuBtn) {
        mainMenuBtn.addEventListener("click", () => {
            UIController.hideOverlay(overlayId);
            setTimeout(() => {
                const gameSceneKeys = ["GameScene", "PPTHeroScene", "MultiplayerGameScene", "MultiplayerGameOverScene"]; // Add GameOver scene itself
                 gameSceneKeys.forEach(key => {
                    if(window.gameInstance?.scene?.isActive(key)) {
                        try {
                             console.log(`Stopping scene: ${key} before going to menu.`);
                             window.gameInstance.scene.stop(key);
                        } catch(e){ console.error(`Error stopping scene ${key}:`, e); }
                    }
                 });

                // Go to appropriate menu based on auth status
                const user = auth.currentUser;
                const targetScene = (user && user.emailVerified) ? "AuthenticatedMenuScene" : "MainMenuScene";
                console.log(`Transitioning to ${targetScene} from Game Over.`);
                window.gameInstance?.scene?.start(targetScene);
            }, 50); // Shorter delay
        });
    }
});

// Export necessary functions
export default {
  showGeneralMessage,
  showGameOverUI,
  formatTime,
  // createLoadingIndicator, // Less needed if using CSS classes
  // removeLoadingIndicator
};